<template>
  <div class="mod-dial lm-dialog" @click="closeSettings" @keydown.esc="onEscape">
    <div
      class="lm-shell"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
      tabindex="-1"
      ref="dialog"
      @click.stop
    >
      <div class="lm-settings-shell">
        <aside class="lm-side">
          <nav class="lm-nav" role="tablist" :aria-label="$t('PROFILE_SETTINGS')">
            <button type="button" role="tab" :aria-selected="activeTab === 'plan'" @click="activeTab = 'plan'">
              {{ $t('PROFILE_TITLE') }}
            </button>
            <button type="button" role="tab" :aria-selected="activeTab === 'access'" @click="activeTab = 'access'">
              {{ $t('ACCESS_MANAGER_RIGHTS_MANAGER') }}
            </button>
            <button type="button" role="tab" :aria-selected="activeTab === 'categories'" @click="activeTab = 'categories'">
              {{ $t('LM_CATEGORY_MANAGER') }}
            </button>
            <button v-if="showApiTab" type="button" role="tab" :aria-selected="activeTab === 'api'" @click="activeTab = 'api'">
              {{ $t('API_CREDENTIALS_TITLE') }}
            </button>
          </nav>
        </aside>
        <div class="lm-main">
          <header class="lm-header">
            <div>
              <h1 id="settings-title">{{ tabTitle }}</h1>
              <p>{{ tabDescription }}</p>
            </div>
            <button type="button" class="lm-close" :aria-label="$t('ACCESS_MANAGER_CLOSE')" @click="closeSettings">
              <i class="bi bi-x-lg"></i>
            </button>
          </header>
          <div class="lm-scroll" role="tabpanel">
            <div v-show="activeTab === 'plan'">
              <div class="lm-account">
                <div>
                  <span class="lm-eyebrow">E-mail</span>
                  <span class="lm-email">{{ getProfile.email }}</span>
                </div>
                <button type="button" class="btn-st-tr" @click.stop="AUTH_LOGOUT">{{ $t('HEADER_LOG_OUT') }}</button>
              </div>
              <div class="lm-plan">
                <div class="lm-plan-top">
                  <div class="lm-plan-name">{{ getProfile.planName }}</div>
                  <span class="lm-plan-status">{{ planStatusLabel }}</span>
                  <button
                    v-if="canCancelSubscription"
                    type="button"
                    class="lm-link"
                    @click="askCancelSubscription"
                  >
                    {{ $t('PRC_CANCEL_SUBSCRIPTION') }}
                  </button>
                </div>
                <div class="lm-usage">
                  <div>
                    <div class="lm-usage-label">
                      <span>{{ $t('PRC_USED') }}</span>
                      <b>{{ getProfile.pageCount }}/{{ getProfile.pageLimit }}</b>
                    </div>
                    <div class="lm-track"><span :style="{ width: usagePercent(getProfile.pageCount, getProfile.pageLimit) }"></span></div>
                  </div>
                  <div>
                    <div class="lm-usage-label">
                      <span>{{ $t('PRC_ADMIN_USED') }}</span>
                      <b>{{ getProfile.adminUserCount }}/{{ getProfile.adminLimit }}</b>
                    </div>
                    <div class="lm-track purple"><span :style="{ width: usagePercent(getProfile.adminUserCount, getProfile.adminLimit) }"></span></div>
                  </div>
                </div>
                <div class="lm-plan-foot">{{ $t('PRC_EXPR') }} <strong>{{ getProfile.expire }}</strong></div>
              </div>
              <h2 class="lm-section-title">{{ $t("AUTH_ACTIVE") }}</h2>
              <SocialAuthButtons variant="cards" :selectedAccessEmail="userEmail" />
            </div>
            <div v-show="activeTab === 'access'">
              <AccessManager />
            </div>
            <div v-show="activeTab === 'categories'">
              <table class="lm-table" v-if="getCategories.length">
                <thead>
                  <tr>
                    <th>{{ $t('CATEGORY_ACCESS_CATEGORY') }}</th>
                    <th>{{ $t('LM_PAGES_IN_CATEGORY') }}</th>
                    <th>{{ $t('ACCESS_MANAGER_ACTION') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="category in getCategories" :key="category.id">
                    <td>
                      <span :class="`cat-indicator ${categoryColor(category.id)}`"></span>
                      {{ category.name }}
                    </td>
                    <td>{{ categoryPageCount(category) }}</td>
                    <td>
                      <button type="button" class="lm-remove" @click="editCategory(category)">{{ $t('LM_EDIT') }}</button>
                      <button type="button" class="lm-remove" @click="askDeleteCategory(category)">{{ $t('DELETE_CONFIRM_DELETE') }}</button>
                      <div v-if="deleteTarget && deleteTarget.id === category.id" class="delete-confirm" @click.stop>
                        <p>{{ $t('LM_DELETE_BODY', { name: category.name }) }}</p>
                        <div class="delete-actions">
                          <button type="button" class="btn-cancel" @click="deleteTarget = null">{{ $t('DELETE_CANCEL') }}</button>
                          <button type="button" class="btn-confirm" @click="deleteCategory">{{ $t('DELETE_CONFIRM_DELETE') }}</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="lm-empty">{{ $t('LM_NO_CATEGORIES') }}</p>
            </div>
            <div v-show="activeTab === 'api' && showApiTab">
              <div class="api-documentation">
                <div class="api-credentials-section">
                  <div class="api-key-container">
                  <span class="info-title">{{ $t('API_KEY_LABEL') }}</span>
                    <div class="api-key-wrapper">
                      <input type="text" readonly :value="userToken" class="api-key-field" />
                      <button class="copy-button" @click="copyToken" :title="$t('COPY_TO_CLIPBOARD')">
                        <i class="bi bi-clipboard"></i>
                      </button>
                    </div>
                  </div>
                  <div class="api-description">
                    <p>{{ $t('API_KEY_DESCRIPTION') }}</p>
                  </div>
                </div>
                
                <div class="api-base-url">
                  <p><span>Base URL:</span> <code>https://api.lucmus.com</code></p>
                </div>
                
                <div class="api-section">
                  <h3 class="api-section-title">{{ $t('API_AUTHORIZATION') }}</h3>
                  <div class="api-section-content">
                    <p>{{ $t('API_AUTH_DESCRIPTION') }}</p>
                    <div class="api-example">
                      <code>Authorization: bearer &lt;ваш_jwt_токен&gt;</code>
                    </div>
                    <p class="api-note">{{ $t('API_AUTH_ERROR') }}</p>
                  </div>
                </div>
                
                <div class="api-section">
                  <h3 class="api-section-title">{{ $t('API_GENERAL_INFO') }}</h3>
                  <div class="api-section-content">
                    <ul class="api-list">
                      <li>{{ $t('API_ALL_GET') }}</li>
                      <li>{{ $t('API_DATE_FORMAT') }}</li>
                      <li>{{ $t('API_JSON_RESPONSE') }}</li>
                </ul>
                  </div>
                </div>
                
                <div class="api-section">
                  <h3 class="api-section-title">{{ $t('API_GET_PAGES') }}</h3>
                  <div class="api-endpoint">
                    <div class="http-method">GET</div>
                    <div class="endpoint-url">/pages/all</div>
                  </div>
                  <div class="api-section-content">
                    <p>{{ $t('API_GET_PAGES_DESC') }}</p>
                    
                    <h4 class="api-subsection-title">{{ $t('API_HEADERS') }}</h4>
                    <div class="parameter-table">
                      <div class="parameter-row">
                        <div class="parameter-name"><strong>Authorization</strong> (required)</div>
                        <div class="parameter-value"><code>bearer &lt;ваш_jwt_токен&gt;</code></div>
                      </div>
                    </div>
                    
                    <h4 class="api-subsection-title">{{ $t('API_REQUEST_PARAMS') }}</h4>
                    <p>{{ $t('API_NO_PARAMS') }}</p>
                    
                    <h4 class="api-subsection-title">{{ $t('API_REQUEST_EXAMPLE') }}</h4>
                    <div class="code-block request">
                      <pre>GET https://api.lucmus.com/pages/all
Authorization: bearer &lt;ваш_jwt_токен&gt;</pre>
                    </div>
                    
                    <h4 class="api-subsection-title">{{ $t('API_RESPONSE_EXAMPLE') }}</h4>
                    <div class="code-block response">
                      <pre>{
  "status": "OK",
  "pages": [
    {
      "page_id": 367,
      "social": "IG",
      "owner_id": "17841400831935560",
      "owner_name": "pepsi",
      "owner_avatar": "https://alpha.popsters.com/monitoring/api/images/avatar/4f45402e87d5261cf92a9c70815d1530.jpg",
      "owner_url": "https://instagram.com/pepsi",
      "hash_id": "5dcccfb3370c4f4c9d8cb33427a4d9",
      "status": "finished",
      "email": "ragul2004@yandex.ru",
      "is_admin": 0,
      "owner_type": "page",
      "owner_domain": "pepsi",
      "need_token_change": 0,
      "error_code": "2500"
    },
    /* Additional entries... */
  ]
}</pre>
                    </div>
                    
                    <h4 class="api-subsection-title">{{ $t('API_KEY_FIELDS') }}</h4>
                    <div class="parameter-list">
                      <div class="parameter-item">
                        <div class="parameter-name"><strong>page_id</strong></div>
                        <div class="parameter-description">{{ $t('API_PAGE_ID_DESC') }}</div>
                      </div>
                      <div class="parameter-item">
                        <div class="parameter-name"><strong>social</strong></div>
                        <div class="parameter-description">{{ $t('API_SOCIAL_DESC') }}</div>
                      </div>
                      <div class="parameter-item">
                        <div class="parameter-name"><strong>hash_id</strong></div>
                        <div class="parameter-description">{{ $t('API_HASH_ID_DESC') }}</div>
                      </div>
                      <!-- Additional fields are available in documentation but shortened for clarity -->
                    </div>
                  </div>
                </div>
                
                <div class="api-section">
                  <h3 class="api-section-title">{{ $t('API_GET_STATS') }}</h3>
                  <div class="api-endpoint">
                    <div class="http-method">GET</div>
                    <div class="endpoint-url">/pages/stats</div>
                  </div>
                  <div class="api-section-content">
                    <p>{{ $t('API_GET_STATS_DESC') }}</p>
                    
                    <h4 class="api-subsection-title">{{ $t('API_HEADERS') }}</h4>
                    <div class="parameter-table">
                      <div class="parameter-row">
                        <div class="parameter-name"><strong>Authorization</strong> (required)</div>
                        <div class="parameter-value"><code>bearer &lt;ваш_jwt_токен&gt;</code></div>
                      </div>
                    </div>
                    
                    <h4 class="api-subsection-title">{{ $t('API_REQUEST_PARAMS') }}</h4>
                    <div class="parameter-table">
                      <div class="parameter-row">
                        <div class="parameter-name"><strong>hash_ids</strong> (string, required)</div>
                        <div class="parameter-description">{{ $t('API_HASH_IDS_DESC') }}</div>
                      </div>
                      <div class="parameter-row">
                        <div class="parameter-name"><strong>start</strong> (string, required)</div>
                        <div class="parameter-description">{{ $t('API_START_DATE_DESC') }}</div>
                      </div>
                      <div class="parameter-row">
                        <div class="parameter-name"><strong>end</strong> (string, required)</div>
                        <div class="parameter-description">{{ $t('API_END_DATE_DESC') }}</div>
                      </div>
                    </div>
                    
                    <h4 class="api-subsection-title">{{ $t('API_REQUEST_EXAMPLE') }}</h4>
                    <div class="code-block request">
                      <pre>GET https://api.lucmus.com/pages/stats?hash_ids=5dcccfb3370c4f4c9d8cb33427a4d9&start=2025-03-01&end=2025-03-23
Authorization: bearer &lt;jwt_token&gt;
Accept: application/json
Content-Type: application/json;charset=UTF-8</pre>
                    </div>
                    
                    <h4 class="api-subsection-title">{{ $t('API_CURL_EXAMPLE') }}</h4>
                    <div class="code-block curl">
                      <pre>curl --location --request GET 'https://api.lucmus.com/pages/stats?hash_ids=5dcccfb3370c4f4c9d8cb33427a4d9&start=2025-03-01&end=2025-03-23' \
--header 'accept: application/json, text/plain, */*' \
--header 'authorization: bearer &lt;jwt_token&gt;' \
--header 'content-type: application/json;charset=UTF-8'</pre>
                    </div>
                  </div>
                </div>
                
                <div class="api-section">
                  <h3 class="api-section-title">{{ $t('API_STATUS_CODES') }}</h3>
                  <div class="api-section-content">
                    <p>{{ $t('API_STATUS_DESC') }}</p>
                    <p>{{ $t('API_AUTH_ERROR_DESC') }}</p>
                  </div>
                </div>
                
                <div class="api-section">
                  <h3 class="api-section-title">{{ $t('API_ADDITIONAL_NOTES') }}</h3>
                  <div class="api-section-content">
                    <ul class="api-list">
                      <li>{{ $t('API_MULTIPLE_HASH_IDS') }}</li>
                      <li>{{ $t('API_EMPTY_RESULT') }}</li>
                      <li>{{ $t('API_DATE_FORMAT_STRICT') }}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <footer class="lm-footer">
            <button type="button" class="btn-st-viol" @click="closeSettings">
              {{ $t('ACCESS_MANAGER_CLOSE') }}
            </button>
          </footer>
        </div>
      </div>
      <ConfirmDialog
        :open="confirmCancel"
        :title="$t('PRC_CANCEL_SUBSCRIPTION')"
        :description="$t('PRC_CANCEL_SUBSCRIPTION_CONFIRM')"
        :confirm-label="$t('PRC_CANCEL_SUBSCRIPTION')"
        :cancel-label="$t('LM_CANCEL')"
        @confirm="cancelSubscription"
        @cancel="confirmCancel = false"
      />
    </div>
  </div>
</template>

<script>
import AccessManager from './AccessManager';
import { mapGetters, mapMutations, mapActions } from "vuex";
import { AUTH_LOGOUT, AUTH_SOCIAL_TOKENS, AUTH_GET_PROFILE, AUTH_CANCEL_SUBSCRIPTION } from "@/store/actions/auth";
import AddPage from './AddPage';
import { INTRO_SET_SETTINGS_MODAL_VISIBLE } from "../../store/actions/intro";
import { CATEGORIES_SET_LIST, CATEGORIES_DELETE, CATEGORIES_SET_CURRENT, CATEGORIES_SET_MANAGER_VISIBLE } from "../../store/actions/categories";
import SocialAuthButtons from '@/components/SocialAuthButtons.vue';
import ConfirmDialog from './ConfirmDialog.vue';

export default {
  name: 'SettingsModal',
  components: {
    AccessManager,
    AddPage,
    SocialAuthButtons,
    ConfirmDialog,
  },
  data() {
    return {
      activeTab: 'plan',
      userToken: '',
      confirmCancel: false,
      deleteTarget: null,
    };
  },
  computed: {
    ...mapGetters(['getProfile','getSocialTokens','getCategories']),
    userEmail() {
      return this.getProfile.email;
    },
    showApiTab() {
      const planCode = this.getProfile?.plan || '';
      return planCode === '300' || planCode === '400';
    },
    tabTitle() {
      if (this.activeTab === 'access') return this.$t('ACCESS_MANAGER_RIGHTS_MANAGER');
      if (this.activeTab === 'categories') return this.$t('LM_CATEGORY_MANAGER');
      if (this.activeTab === 'api') return this.$t('API_CREDENTIALS_TITLE');
      return this.$t('PROFILE_TITLE');
    },
    tabDescription() {
      if (this.activeTab === 'access') return this.$t('LM_ACCESS_DESC');
      if (this.activeTab === 'categories') return this.$t('LM_CATEGORY_MANAGER_DESC');
      if (this.activeTab === 'api') return this.$t('LM_API_DESC');
      return this.$t('LM_PROFILE_DESC');
    },
    canCancelSubscription() {
      const profile = this.getProfile || {};
      const paid = profile.payment_system === 'stripe' || profile.payment_system === 'cloudpayments';
      return paid && profile.subscription_status !== 'cancelled';
    },
    planStatusLabel() {
      const profile = this.getProfile || {};
      if (profile.subscription_status === 'cancelled') return this.$t('PRC_CANCELLED');
      if (profile.payment_system === 'trial') return this.$t('LM_TRIAL');
      return this.$t('LM_ACTIVE');
    },
  },
  methods: {
    ...mapMutations({
      INTRO_SET_SETTINGS_MODAL_VISIBLE: INTRO_SET_SETTINGS_MODAL_VISIBLE,
      CATEGORIES_SET_CURRENT,
      CATEGORIES_SET_MANAGER_VISIBLE,
    }),
    ...mapActions([AUTH_LOGOUT, AUTH_GET_PROFILE, AUTH_CANCEL_SUBSCRIPTION, CATEGORIES_SET_LIST, CATEGORIES_DELETE]),
    
    async copyToken() {
      try {
        await navigator.clipboard.writeText(this.userToken);
        this.$store.dispatch('alerts/addAlert', {
          message: this.$t('COPIED_TO_CLIPBOARD'),
          color: 'success',
          duration: 3000
        }, { root: true });
      } catch (err) {
        this.$store.dispatch('alerts/addAlert', {
          message: this.$t('COPY_FAILED'),
          color: 'danger',
          duration: 3000
        }, { root: true });
      }
    },
    
    closeSettings() {
      if (this.confirmCancel || this.deleteTarget) return;
      this.INTRO_SET_SETTINGS_MODAL_VISIBLE(false);
    },
    onEscape() {
      if (this.confirmCancel || this.deleteTarget) return;
      this.closeSettings();
    },
    categoryPageCount(category) {
      if (!category) return 0;
      if (category.pages && category.pages.length) {
        return category.pages.filter(page => page.status !== 'deleted').length;
      }
      return (category.page_ids || []).length;
    },
    categoryColor(categoryId) {
      const stored = localStorage.getItem('categoryColors');
      const colors = stored ? JSON.parse(stored) : {};
      return (colors[categoryId] || 'gray') + '-back';
    },
    editCategory(category) {
      this.CATEGORIES_SET_CURRENT(category);
      this.CATEGORIES_SET_MANAGER_VISIBLE(true);
    },
    askDeleteCategory(category) {
      this.deleteTarget = category;
    },
    async deleteCategory() {
      const target = this.deleteTarget;
      this.deleteTarget = null;
      if (!target) return;
      try {
        await this[CATEGORIES_DELETE](target.id);
      } catch (error) {
        this.$store.dispatch('alerts/addAlert', {
          message: this.$t('CATEGORY_MANAGER_DELETE_ERROR'),
          color: 'danger',
          duration: 5000
        }, { root: true });
      }
    },
    usagePercent(used, limit) {
      const total = Number(limit) || 0;
      if (!total) return '0%';
      return `${Math.min(100, Math.round((Number(used) || 0) / total * 100))}%`;
    },
    askCancelSubscription() {
      this.confirmCancel = true;
    },
    async cancelSubscription() {
      this.confirmCancel = false;
      try {
        await this.$store.dispatch(AUTH_CANCEL_SUBSCRIPTION);
        
        // Show success message
        this.$store.dispatch('alerts/addAlert', {
          message: this.$t('PRC_SUBSCRIPTION_CANCELLED_SUCCESS'),
          color: 'success',
          duration: 5000
        }, { root: true });
      } catch (error) {
        // Show error message
        this.$store.dispatch('alerts/addAlert', {
          message: error.error_message || this.$t('PRC_CANCELLATION_ERROR'),
          color: 'danger',
          duration: 5000
        }, { root: true });
      }
    }
  },
  async mounted() {
    if (!this.getSocialTokens || Object.keys(this.getSocialTokens).length === 0) {
      await this.$store.dispatch(AUTH_SOCIAL_TOKENS);
    }
    // Get token from localStorage
    try {
      this.userToken = localStorage.getItem('user-token') || '';
    } catch (e) {
      this.userToken = '';
    }
    
    // Redirect from API tab if not allowed
    if (this.activeTab === 'api' && !this.showApiTab) {
      this.activeTab = 'plan';
    }
    this.$nextTick(() => {
      if (this.$refs.dialog) this.$refs.dialog.focus();
    });
    this[CATEGORIES_SET_LIST]();
  },
  watch: {
    activeTab(tab) {
      if (tab === 'categories') this[CATEGORIES_SET_LIST]();
    },
    // Watch for plan changes to redirect if needed
    'getProfile.planName'() {
      if (this.activeTab === 'api' && !this.showApiTab) {
        this.activeTab = 'plan';
      }
    }
  }
};
</script>
